/**
 * PortfolioStrategy call builder.
 *
 * InitParams (Solidity): (address asset, address swapAdapter, address chainlinkVerifier,
 *   address[] tokens, uint256[] weightsBps, uint256 totalAmount, uint256 maxSlippageBps,
 *   bytes[] swapExtraData, uint8[] priceDecimals, bytes32[] feedIds)
 *
 * `priceDecimals[i]` declares the raw Chainlink feed scale for allocation `i`
 * (typically 8 for tokenized stocks, 18 for crypto pairs). It normalizes the
 * position value into the vault asset's decimals.
 *
 * `feedIds[i]` binds each allocation to its expected Chainlink feed (Sherlock
 * #56). Two modes, selected by `chainlinkVerifier`:
 *   - Data Streams (chainlinkVerifier != 0): feedIds[i] is the report feed id.
 *   - Push feeds (chainlinkVerifier == 0, e.g. Robinhood mainnet): feedIds[i]
 *     is an AggregatorV3 proxy address left-padded to bytes32
 *     (`bytes32(uint160(feed))`); the contract asserts the feed's live
 *     `decimals()` equals `priceDecimals[i]`.
 * feedIds[i] must be non-zero in both modes.
 */

import type { Address, Hex } from "viem";
import { concat, encodeAbiParameters, encodeFunctionData, numberToHex, pad } from "viem";
import { ERC20_ABI, BASE_STRATEGY_ABI } from "../lib/abis.js";
import type { BatchCall } from "../lib/batch.js";

// ── UniswapSwapAdapter swapExtraData builders ──
//
// Wire format for every mode: one raw mode byte, then abi-encoded route data
// (see UniswapSwapAdapter.sol header):
//   Mode 0 — v3 single-hop:  0x00 + abi.encode(uint24 fee)
//   Mode 1 — v3 multi-hop:   0x01 + abi.encode(bytes path, uint16 perHopSlippageBps)
//   Mode 2 — v4 single-hop:  0x02 + abi.encode(uint24 fee, int24 tickSpacing)
//   Mode 3 — v4 multi-hop:   0x03 + abi.encode(PathHop[])  where
//            PathHop = { address currency; uint24 fee; int24 tickSpacing }
//            hop i swaps prev currency → hops[i].currency; native ETH = address(0)
//            allowed as an intermediate; last hop currency MUST equal the token.

const ZERO_ADDRESS: Address = "0x0000000000000000000000000000000000000000";

/** Concatenate a raw 1-byte mode prefix with abi-encoded route data. */
function withMode(mode: number, routeData: Hex): Hex {
  const modeByte = (`0x${mode.toString(16).padStart(2, "0")}`) as Hex;
  return concat([modeByte, routeData]);
}

/** Mode 0 — Uniswap v3 single-hop. */
export function v3SingleHopExtraData(fee: number): Hex {
  return withMode(0, encodeAbiParameters([{ type: "uint24" }], [fee]));
}

/** Mode 2 — Uniswap v4 single-hop (hookless). */
export function v4SingleHopExtraData(fee: number, tickSpacing: number): Hex {
  return withMode(
    2,
    encodeAbiParameters(
      [{ type: "uint24" }, { type: "int24" }],
      [fee, tickSpacing],
    ),
  );
}

export interface V4PathHop {
  currency: Address; // address(0) = native ETH; last hop MUST equal the allocation token
  fee: number;
  tickSpacing: number;
}

/** Mode 3 — Uniswap v4 multi-hop (hookless; native ETH allowed as intermediate). */
export function v4PathExtraData(hops: V4PathHop[]): Hex {
  // The adapter's _orientHops reverts InvalidPath for n < 2 (a single hop is
  // mode 2) — reject at build time instead of at first execution.
  if (hops.length < 2) throw new Error("v4PathExtraData: mode 3 needs >= 2 hops (use v4SingleHopExtraData for one)");
  return withMode(
    3,
    encodeAbiParameters(
      [
        {
          type: "tuple[]",
          components: [
            { type: "address", name: "currency" },
            { type: "uint24", name: "fee" },
            { type: "int24", name: "tickSpacing" },
          ],
        },
      ],
      [hops.map((h) => ({ currency: h.currency, fee: h.fee, tickSpacing: h.tickSpacing }))],
    ),
  );
}

export { ZERO_ADDRESS as NATIVE_ETH };

export interface BasketAllocation {
  token: Address;
  weightBps: number; // e.g. 4000 = 40%
  swapExtraData: Hex; // adapter-specific (fee tier, path, etc.)
  priceDecimals: number; // Chainlink feed report decimals (8 or 18)
  feedId: Hex; // bytes32: Data Streams feed id, or bytes32(uint160(pushFeed))
}

/** Default max price age (seconds) for a tokenized-equity push feed on Robinhood
 * mainnet: 96h. Equity feeds go ~77h stale over holiday weekends, past the
 * contract's 26h default — so we pack a wider staleness window per equity slot.
 * Crypto/FX/stable feeds pass 0 (→ contract default 26h). */
export const EQUITY_MAX_PRICE_AGE_SECONDS = 96 * 60 * 60; // 345_600

// Contract-enforced bounds on a packed per-slot max price age (0 = default).
const MIN_MAX_PRICE_AGE = 3600; // 1h
const MAX_MAX_PRICE_AGE = 2_592_000; // 30d

/**
 * Pack a Chainlink AggregatorV3 proxy address (+ optional per-slot max price age)
 * into a bytes32 feed id for push-feed mode (`chainlinkVerifier == address(0)`):
 *
 *   bytes32 = uint256(maxAgeSeconds) << 160 | uint160(feedAddress)
 *
 * `maxAgeSeconds`:
 *   - omitted / 0  → lower 160 bits only (contract falls back to its 26h default;
 *                    byte-identical to the plain left-padded address).
 *   - provided     → must be within [3600, 2_592_000] seconds.
 *
 * The contract decodes the address via `address(uint160(uint256(feedIds[i])))`
 * and the age via `uint256(feedIds[i]) >> 160`.
 */
export function pushFeedId(feed: Address, maxAgeSeconds?: number): Hex {
  if (maxAgeSeconds === undefined || maxAgeSeconds === 0) {
    return pad(feed, { size: 32 });
  }
  if (
    !Number.isInteger(maxAgeSeconds) ||
    maxAgeSeconds < MIN_MAX_PRICE_AGE ||
    maxAgeSeconds > MAX_MAX_PRICE_AGE
  ) {
    throw new Error(
      `pushFeedId: maxAgeSeconds must be an integer in [${MIN_MAX_PRICE_AGE}, ${MAX_MAX_PRICE_AGE}] (or 0 for the contract default), got ${maxAgeSeconds}`,
    );
  }
  // (age << 160) | uint160(addr), as a 32-byte hex string.
  const packed = (BigInt(maxAgeSeconds) << 160n) | BigInt(feed);
  return numberToHex(packed, { size: 32 });
}

export function buildInitData(
  asset: Address,
  swapAdapter: Address,
  chainlinkVerifier: Address,
  allocations: BasketAllocation[],
  totalAmount: bigint,
  maxSlippageBps: number,
): Hex {
  const tokens = allocations.map((a) => a.token);
  const weightsBps = allocations.map((a) => BigInt(a.weightBps));
  const swapExtraData = allocations.map((a) => a.swapExtraData);
  const priceDecimals = allocations.map((a) => a.priceDecimals);
  const feedIds = allocations.map((a) => a.feedId);

  return encodeAbiParameters(
    [
      { type: "address" },
      { type: "address" },
      { type: "address" },
      { type: "address[]" },
      { type: "uint256[]" },
      { type: "uint256" },
      { type: "uint256" },
      { type: "bytes[]" },
      { type: "uint8[]" },
      { type: "bytes32[]" },
    ],
    [
      asset,
      swapAdapter,
      chainlinkVerifier,
      tokens,
      weightsBps,
      totalAmount,
      BigInt(maxSlippageBps),
      swapExtraData,
      priceDecimals,
      feedIds,
    ],
  );
}

export function buildExecuteCalls(
  clone: Address,
  asset: Address,
  totalAmount: bigint,
): BatchCall[] {
  return [
    {
      target: asset,
      data: encodeFunctionData({
        abi: ERC20_ABI,
        functionName: "approve",
        args: [clone, totalAmount],
      }),
      value: 0n,
    },
    {
      target: clone,
      data: encodeFunctionData({
        abi: BASE_STRATEGY_ABI,
        functionName: "execute",
      }),
      value: 0n,
    },
  ];
}

export function buildSettleCalls(clone: Address): BatchCall[] {
  return [
    {
      target: clone,
      data: encodeFunctionData({
        abi: BASE_STRATEGY_ABI,
        functionName: "settle",
      }),
      value: 0n,
    },
  ];
}

export function buildUpdateParamsCalls(
  clone: Address,
  newWeightsBps: number[],
  newMaxSlippageBps: number,
  newSwapExtraData: Hex[],
): BatchCall[] {
  const data = encodeAbiParameters(
    [{ type: "uint256[]" }, { type: "uint256" }, { type: "bytes[]" }],
    [
      newWeightsBps.map((w) => BigInt(w)),
      BigInt(newMaxSlippageBps),
      newSwapExtraData,
    ],
  );

  return [
    {
      target: clone,
      data: encodeFunctionData({
        abi: [
          {
            type: "function",
            name: "updateParams",
            inputs: [{ type: "bytes", name: "data" }],
            outputs: [],
            stateMutability: "nonpayable",
          },
        ],
        functionName: "updateParams",
        args: [data],
      }),
      value: 0n,
    },
  ];
}

// ── Rebalancing ──

import { PORTFOLIO_STRATEGY_ABI } from "../lib/abis.js";

/**
 * Encode calldata for the simple `rebalance()` call.
 * This is called directly by the proposer (not via vault batch).
 */
export function encodeRebalanceCalldata(): Hex {
  return encodeFunctionData({
    abi: PORTFOLIO_STRATEGY_ABI,
    functionName: "rebalance",
  });
}

/**
 * Encode calldata for `rebalanceDelta(bytes[] priceReports)`.
 *
 * Two modes, keyed off `basketSize` + `pushMode`:
 *   - Push-feed mode (Robinhood mainnet, chainlinkVerifier == 0): the strategy
 *     reads prices from its bound AggregatorV3 push feeds and IGNORES the report
 *     bytes, but the array length must still equal the basket size or the call
 *     reverts with `LengthMismatch`. Pass `pushMode: true` and the CLI fills
 *     `Array(basketSize).fill("0x")` — N empty entries. (Real Data Streams
 *     reports here would revert with `InvalidFeedId`.)
 *   - Data Streams mode (chainlinkVerifier != 0): pass the signed report blobs
 *     as `reports` (one per allocation).
 */
export function encodeRebalanceDeltaCalldata(
  opts: { basketSize: number; pushMode: true } | { reports: Hex[]; pushMode?: false },
): Hex {
  const priceReports: Hex[] =
    "pushMode" in opts && opts.pushMode
      ? (Array(opts.basketSize).fill("0x") as Hex[])
      : (opts.reports as Hex[]);
  return encodeFunctionData({
    abi: PORTFOLIO_STRATEGY_ABI,
    functionName: "rebalanceDelta",
    args: [priceReports],
  });
}

/**
 * Encode calldata for `updateParams()` to change weights before rebalancing.
 */
export function encodeUpdateWeightsCalldata(
  newWeightsBps: number[],
  newMaxSlippageBps: number,
  newSwapExtraData: Hex[],
): Hex {
  const data = encodeAbiParameters(
    [{ type: "uint256[]" }, { type: "uint256" }, { type: "bytes[]" }],
    [
      newWeightsBps.map((w) => BigInt(w)),
      BigInt(newMaxSlippageBps),
      newSwapExtraData,
    ],
  );

  return encodeFunctionData({
    abi: PORTFOLIO_STRATEGY_ABI,
    functionName: "updateParams",
    args: [data],
  });
}
